import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

/**
 * A camada de dados das configurações administrativas.
 *
 * As oito telas de `/admin/configuracoes` leem da mesma tabela
 * chave-valor (`configuracoes`, migração `0002_dominio_base.sql`: `chave text` e
 * `valor jsonb`), então a camada de dados é uma só. Fosse um arquivo por tela,
 * a mesma consulta apareceria oito vezes — e duas repetições já pedem extração
 * (CLAUDE.md, "Regras de interface", item 1).
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 * `/configuracoes` ainda não existe na API. A tabela existe no banco, mas
 * nenhuma rota a expõe e não há política de escrita. Decisão do dono de
 * 2026-08-31 (CLAUDE.md, "Proibido", exceção estendida às telas de configuração
 * e parametrização) — mesmas três condições de sempre: só leitura, dado
 * claramente fictício, removido antes de `develop` → `hml` (`git grep -n
 * "TEMPORÁRIO" apps/web/src`, checklist em `docs/ALVOS.md`). Nenhum "Salvar"
 * destas telas grava coisa alguma.
 *
 * Os valores devolvidos aqui são os PADRÕES do sistema, não invenção: é o que
 * cada regra usa hoje quando não há configuração gravada. Quem escrever a API
 * depois encontra em `CHAVES` o contrato que a interface espera.
 */

/**
 * As chaves usadas pelas telas de configuração, com o formato de cada valor.
 *
 * Uma chave por assunto, como na tabela. O nome é o mesmo que a API deve usar.
 */
export const CHAVES = {
  identidadeVisual: 'identidade_visual',
  rastreamento: 'scripts_rastreamento',
  inscricao: 'inscricao_credenciamento',
  prazoFechamento: 'fechamento_prazo',
  desvioAlerta: 'desvio_alerta_lote',
  ateste: 'ateste_parametros',
  matrizAcoesLote: 'fechamento_matriz_acoes',
  slasInstitucionais: 'slas_institucionais',
} as const;

/** Identidade visual e metadados do portal público. */
export interface IdentidadeVisual {
  favicon_url: string;
  og_image_url: string;
  site_title: string;
  site_description: string;
}

/** Identificadores das ferramentas de medição de uso. */
export interface ScriptsDeRastreamento {
  google_analytics_id: string;
  microsoft_clarity_id: string;
}

/** Regras da inscrição de credenciamento. */
export interface ConfiguracaoDeInscricao {
  max_escolas_por_inscricao: number;
}

/** Dia limite do mês seguinte, por natureza da pessoa. */
export interface PrazoDeFechamento {
  /** Pessoa física. */
  pf: number;
  /** Pessoa jurídica e cooperativa. */
  pj: number;
}

/** Percentual de variação que dispara alerta visual na aprovação de lote. */
export interface DesvioDeAlerta {
  pct: number;
}

/** Limites e janela usados para sinalizar medição anormal no ateste. */
export interface ParametrosDeAteste {
  desvio_valor_pct: number;
  desvio_alunos_pct: number;
  baseline_meses: number;
}

/** As ações em lote do cockpit de fechamento. */
export const ACOES_EM_LOTE = [
  {
    id: 'notificar_sme',
    rotulo: 'Notificar SME',
    descricao: 'Envia o evento de cobrança para a SME pela fila de saída.',
  },
  {
    id: 'exportar_sof',
    rotulo: 'Exportar lote SOF',
    descricao: 'Gera o lote de empenho para o sistema orçamentário.',
  },
  {
    id: 'escalonar_dre',
    rotulo: 'Escalonar cobrança DRE',
    descricao: 'Abre demanda de escalonamento de apontamento para a DRE.',
  },
] as const;

export type AcaoEmLote = (typeof ACOES_EM_LOTE)[number]['id'];

/** As etapas do ciclo de fechamento mensal, na ordem em que acontecem. */
export const ETAPAS_DE_FECHAMENTO = [
  { id: 'abertura', rotulo: 'Aberto' },
  { id: 'apontamento', rotulo: 'Em apontamento' },
  { id: 'validacao', rotulo: 'Em validação SME' },
  { id: 'processamento', rotulo: 'Em processamento' },
  { id: 'pagamento', rotulo: 'Em pagamento' },
  { id: 'concluido', rotulo: 'Concluído' },
] as const;

export type EtapaDeFechamento = (typeof ETAPAS_DE_FECHAMENTO)[number]['id'];

/** Em quais etapas cada ação em lote fica disponível. */
export type MatrizDeAcoes = Record<AcaoEmLote, EtapaDeFechamento[]>;

/**
 * Os prazos institucionais parametrizáveis da apuração mensal.
 *
 * Os quatro abaixo, e os valores padrão, foram confirmados pelo cliente em
 * 11/06/2026 — fato de negócio, não dado inventado. São iguais para todas as
 * DREs, contados em dias úteis.
 */
export const CATALOGO_DE_SLAS = [
  {
    chave: 'envio_dre_sme',
    titulo: 'Envio DRE para SME',
    descricao: 'Prazo da DRE para enviar o apontamento da competência à SME.',
    padrao: 4,
  },
  {
    chave: 'analise_sme',
    titulo: 'Análise da SME',
    descricao: 'Prazo da SME para analisar o apontamento recebido e devolver ou aprovar.',
    padrao: 2,
  },
  {
    chave: 'correcao_dre',
    titulo: 'Correção pela DRE',
    descricao: 'Prazo da DRE para corrigir o apontamento devolvido pela SME.',
    padrao: 1,
  },
  {
    chave: 'documentos_credenciado',
    titulo: 'Documentos do credenciado',
    descricao: 'Prazo do credenciado para apresentar documentação exigida.',
    padrao: 5,
  },
] as const;

export type ChaveDeSla = (typeof CATALOGO_DE_SLAS)[number]['chave'];

export type SlasInstitucionais = Record<ChaveDeSla, number>;

/** O padrão de cada SLA, para "Restaurar padrão" e para o recuo da leitura. */
export const SLAS_PADRAO: SlasInstitucionais = CATALOGO_DE_SLAS.reduce(
  (acumulado, definicao) => ({ ...acumulado, [definicao.chave]: definicao.padrao }),
  {} as SlasInstitucionais,
);

/**
 * Um trabalho agendado e a saúde da sua última execução.
 *
 * Não há tabela correspondente neste repositório: o agendamento ainda não foi
 * construído. O contrato abaixo é o que a tela precisa, registrado para quem
 * for implementá-lo.
 */
export interface TrabalhoAgendado {
  id: string;
  nome: string;
  /** Expressão de agendamento, em formato cron. */
  agenda: string;
  /** Descrição da agenda em português, já pronta para exibir. */
  agenda_descrita: string;
  ativo: boolean;
  ultima_execucao: string | null;
  proxima_execucao: string | null;
  ultima_situacao: 'sucesso' | 'falha' | null;
  duracao_ms: number | null;
  sucessos_24h: number;
  falhas_24h: number;
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Tipado como `unknown` por valor porque a tabela é chave-valor de verdade
// (`valor jsonb`): cada chave tem um formato próprio, e é `useConfiguracao<T>`
// que diz qual. O `satisfies` em cada linha garante que o formato escrito aqui
// bate com a interface declarada acima.
const CONFIGURACOES_MOCK: Record<string, unknown> = {
  [CHAVES.identidadeVisual]: {
    favicon_url: '',
    og_image_url: '',
    site_title: 'TEG — Transporte Escolar Gratuito',
    site_description:
      'Programa de Transporte Escolar Gratuito da Secretaria Municipal de Mobilidade Urbana e Transporte.',
  } satisfies IdentidadeVisual,
  [CHAVES.rastreamento]: {
    google_analytics_id: '',
    microsoft_clarity_id: '',
  } satisfies ScriptsDeRastreamento,
  [CHAVES.inscricao]: {
    max_escolas_por_inscricao: 3,
  } satisfies ConfiguracaoDeInscricao,
  [CHAVES.prazoFechamento]: { pf: 25, pj: 25 } satisfies PrazoDeFechamento,
  [CHAVES.desvioAlerta]: { pct: 15 } satisfies DesvioDeAlerta,
  [CHAVES.ateste]: {
    desvio_valor_pct: 20,
    desvio_alunos_pct: 15,
    baseline_meses: 6,
  } satisfies ParametrosDeAteste,
  [CHAVES.matrizAcoesLote]: {
    notificar_sme: ['validacao', 'processamento'],
    exportar_sof: ['processamento', 'pagamento'],
    escalonar_dre: ['apontamento', 'validacao'],
  } satisfies MatrizDeAcoes,
  [CHAVES.slasInstitucionais]: SLAS_PADRAO,
};

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Cinco trabalhos cobrindo os ramos da tela: sucesso, falha, nunca executado e
// inativo. Nomes e agendas são de exemplo — o agendador ainda não existe.
const TRABALHOS_MOCK: TrabalhoAgendado[] = [
  {
    id: 'job-exemplo-01',
    nome: 'alertar_vencimento_documentos',
    agenda: '0 6 * * *',
    agenda_descrita: 'Todo dia, às 6h',
    ativo: true,
    ultima_execucao: '2026-08-31T06:00:12Z',
    proxima_execucao: '2026-09-01T06:00:00Z',
    ultima_situacao: 'sucesso',
    duracao_ms: 2840,
    sucessos_24h: 1,
    falhas_24h: 0,
  },
  {
    id: 'job-exemplo-02',
    nome: 'escalonar_apontamento_vencido',
    agenda: '0 */4 * * *',
    agenda_descrita: 'A cada 4 horas',
    ativo: true,
    ultima_execucao: '2026-08-31T12:00:03Z',
    proxima_execucao: '2026-08-31T16:00:00Z',
    ultima_situacao: 'falha',
    duracao_ms: 512,
    sucessos_24h: 4,
    falhas_24h: 2,
  },
  {
    id: 'job-exemplo-03',
    nome: 'fechar_competencia_mensal',
    agenda: '0 3 1 * *',
    agenda_descrita: 'Todo dia 1º, às 3h',
    ativo: true,
    ultima_execucao: '2026-08-01T03:00:44Z',
    proxima_execucao: '2026-09-01T03:00:00Z',
    ultima_situacao: 'sucesso',
    duracao_ms: 94200,
    sucessos_24h: 0,
    falhas_24h: 0,
  },
  {
    id: 'job-exemplo-04',
    nome: 'sincronizar_referencias_transporte',
    agenda: '30 2 * * 1',
    agenda_descrita: 'Toda segunda-feira, às 2h30',
    ativo: true,
    ultima_execucao: null,
    proxima_execucao: '2026-09-07T02:30:00Z',
    ultima_situacao: null,
    duracao_ms: null,
    sucessos_24h: 0,
    falhas_24h: 0,
  },
  {
    id: 'job-exemplo-05',
    nome: 'expurgar_rascunhos_abandonados',
    agenda: '0 4 * * 0',
    agenda_descrita: 'Todo domingo, às 4h',
    ativo: false,
    ultima_execucao: '2026-08-24T04:00:07Z',
    proxima_execucao: null,
    ultima_situacao: 'sucesso',
    duracao_ms: 15300,
    sucessos_24h: 0,
    falhas_24h: 0,
  },
];

export const CHAVE_DE_CONFIGURACAO = (chave: string) => ['configuracoes', chave] as const;
export const CHAVE_DE_TRABALHOS_AGENDADOS = ['trabalhos-agendados', 'lista'] as const;

/**
 * O valor de uma chave de configuração.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend: devolve o
 * padrão do sistema em vez de buscar na API, que ainda não existe.
 */
export function useConfiguracao<T>(chave: string): UseQueryResult<T, ErroApi> {
  return useQuery<T, ErroApi>({
    queryKey: CHAVE_DE_CONFIGURACAO(chave),
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => CONFIGURACOES_MOCK[chave] as T,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Os trabalhos agendados e a saúde de cada um.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
export function useTrabalhosAgendados(): UseQueryResult<TrabalhoAgendado[], ErroApi> {
  return useQuery<TrabalhoAgendado[], ErroApi>({
    queryKey: CHAVE_DE_TRABALHOS_AGENDADOS,
    // TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
    queryFn: async () => TRABALHOS_MOCK,
    retry: naoInsistirEmRecusa,
  });
}

/** Duração legível: milissegundos, segundos ou minutos, conforme a grandeza. */
export function formatarDuracao(ms: number | null): string {
  if (ms == null) {
    return '—';
  }
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)} s`;
  }
  return `${(ms / 60_000).toFixed(1)} min`;
}
